/**
 * Placement tables from @mp/art (`shopLayout`, `streetLayout`), loaded lazily so a missing or
 * partial art package never blocks the game; built-in copies are the fallback.
 * Shop placements are normalised to the base image: x/y = top-left, w/h = size fractions.
 */
export interface Placement {
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
}

export type Pt = readonly [number, number];

export interface StreetPlan {
  /** canvas size the coordinates refer to */
  w: number;
  h: number;
  /** road centre samples, bottom (level 1) → top */
  path: Pt[];
  /** facade bottom-centre anchor per shop id */
  plots: Record<string, { x: number; y: number; scale: number }>;
  facade: { w: number; h: number };
}

const CANVAS = { w: 1024, h: 768 };

/** Built-in copy of the art package's 1024×768 slot rectangles (top-left origin). */
const DEFAULT_SHOP: Record<string, [number, number, number, number, number]> = {
  upg06: [0, 552, 1024, 216, 1],
  upg07: [92, 232, 336, 304, 2],
  upg09: [572, 222, 264, 150, 2],
  upg10: [862, 228, 148, 324, 2],
  upg05: [140, 150, 744, 160, 3],
  upg01: [222, 478, 580, 236, 5],
  upg04: [574, 338, 236, 176, 6],
  upg03: [0, 84, 1024, 128, 7],
  upg02: [312, 0, 400, 128, 8],
  upg08: [12, 452, 224, 308, 9],
};

const DEFAULT_PATH: Pt[] = [
  [512, 2060],
  [470, 1930],
  [330, 1780],
  [380, 1620],
  [660, 1500],
  [700, 1330],
  [420, 1190],
  [330, 1030],
  [560, 890],
  [700, 740],
  [560, 600],
  [480, 470],
];
const DEFAULT_PLOTS: Pt[] = [
  [810, 1990],
  [170, 1640],
  [860, 1390],
  [160, 1000],
  [860, 700],
];

type ArtModule = {
  shopLayout?: Record<string, { x: number; y: number; w: number; h: number; z?: number }>;
  SHOP_CANVAS?: { w: number; h: number };
  streetLayout?: (city: string) => { path: readonly Pt[]; plots: Record<string, { x: number; y: number; scale: number }> };
  STREET?: { w: number; h: number };
  FACADE?: { w: number; h: number };
  samplePath?: (pts: readonly Pt[], per?: number) => Pt[];
};

const artModule = import.meta.glob<ArtModule>('../../../../packages/art/src/index.ts');
let cached: Promise<ArtModule> | null = null;

function loadArt(): Promise<ArtModule> {
  const loader = Object.values(artModule)[0];
  cached ??= loader ? loader().catch(() => ({})) : Promise.resolve({});
  return cached;
}

export type ShopLookup = (slot: string) => Placement;

export async function shopLayoutLookup(): Promise<ShopLookup> {
  const m = await loadArt();
  const canvas = m.SHOP_CANVAS ?? CANVAS;
  return (slot) => {
    const r = m.shopLayout?.[slot];
    const d = DEFAULT_SHOP[slot] ?? DEFAULT_SHOP.upg01!;
    const [x, y, w, h, z] = r ? [r.x, r.y, r.w, r.h, r.z ?? 3] : d;
    return { x: x / canvas.w, y: y / canvas.h, w: w / canvas.w, h: h / canvas.h, z };
  };
}

function catmull(pts: readonly Pt[], per = 12): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[Math.min(pts.length - 1, i + 2)]!;
    for (let j = 0; j < per; j++) {
      const t = j / per;
      const c = (a: number, b: number, cc: number, dd: number) =>
        0.5 * (2 * b + (-a + cc) * t + (2 * a - 5 * b + 4 * cc - dd) * t * t + (-a + 3 * b - 3 * cc + dd) * t * t * t);
      out.push([c(p0[0], p1[0], p2[0], p3[0]), c(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  out.push(pts[pts.length - 1]!);
  return out;
}

export async function streetPlan(city: string, shopIds: string[]): Promise<StreetPlan> {
  const m = await loadArt();
  const size = m.STREET ?? { w: 1024, h: 2048 };
  const facade = m.FACADE ?? { w: 320, h: 400 };
  const fromArt = m.streetLayout?.(city);
  if (fromArt && Object.keys(fromArt.plots).length) return { ...size, path: [...fromArt.path], plots: fromArt.plots, facade };
  const plots: StreetPlan['plots'] = {};
  shopIds.forEach((id, i) => {
    const p = DEFAULT_PLOTS[i] ?? DEFAULT_PLOTS[DEFAULT_PLOTS.length - 1]!;
    plots[id] = { x: p[0], y: p[1], scale: 0.9 };
  });
  return { ...size, path: catmull(DEFAULT_PATH), plots, facade };
}

/** Arc-length parametrisation of a polyline: point at distance s from the start. */
export function pathAt(path: Pt[], s: number): { x: number; y: number } {
  let acc = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]!;
    const b = path[i + 1]!;
    const seg = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (acc + seg >= s) {
      const k = seg ? (s - acc) / seg : 0;
      return { x: a[0] + (b[0] - a[0]) * k, y: a[1] + (b[1] - a[1]) * k };
    }
    acc += seg;
  }
  const last = path[path.length - 1]!;
  return { x: last[0], y: last[1] };
}

export function pathLength(path: Pt[]): number {
  let acc = 0;
  for (let i = 0; i < path.length - 1; i++) acc += Math.hypot(path[i + 1]![0] - path[i]![0], path[i + 1]![1] - path[i]![1]);
  return acc;
}
